import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { pub, BACKEND } from "@/lib/api";

const Ctx = createContext(null);
export const useSamples = () => useContext(Ctx);

export function SamplesProvider({ children }) {
  const [samples, setSamples] = useState([]);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null);
  const limit = useRef(0);
  const [clip, setClip] = useState(null);

  useEffect(() => {
    const load = () => pub.get("/public/samples").then((r) => setSamples(r.data)).catch(() => {});
    load();
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    audio.current = new Audio();
    audio.current.onended = () => setPlaying(false);
    audio.current.onpause = () => setPlaying(false);
    audio.current.ontimeupdate = () => {
      const a = audio.current;
      if (limit.current && a.currentTime >= limit.current) { a.pause(); a.currentTime = 0; }
    };
    return () => { audio.current?.pause(); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const start = (s, secs) => {
    const a = audio.current;
    limit.current = secs;
    const src = `${BACKEND}${s.audio_url}`;
    if (a.src !== src) a.src = src;
    else a.load();
    return a.play().then(() => setPlaying(true));
  };

  const playClip = (key, secs) => {
    const s = samples.find((x) => x.key === key);
    if (playing && clip === key) { audio.current.pause(); return Promise.resolve(); }
    if (!s?.audio_url) return Promise.reject(new Error("not ready"));
    setClip(key);
    return start(s, secs).catch((e) => { setPlaying(false); throw e; });
  };

  const play = (idx = current) => {
    const s = samples[idx];
    setCurrent(idx);
    if (!s) return;
    const a = audio.current;
    if (playing && idx === current && !clip) {
      a.pause();
      return;
    }
    if (!s.audio_url) {
      toast("Sample voice is being prepared", { description: "The Telugu call text is shown on the card." });
      return;
    }
    document.querySelectorAll("audio, video").forEach((m) => m.pause());
    setClip(null);
    start(s, 0).catch(() => setPlaying(false));
  };

  const select = (idx) => {
    audio.current?.pause();
    setCurrent(idx);
  };

  const stop = useCallback(() => audio.current?.pause(), []);

  return <Ctx.Provider value={{ samples, current, playing: playing && !clip, clipPlaying: playing && !!clip, play, select, stop, playClip, clip }}>{children}</Ctx.Provider>;
}
